import { useState, useRef } from 'react';
import {
  uploadResume,
  parseResume,
  analyzeResume,
  resumeChat,
  generateInterviewQuestions,
  matchJobDescription,
  improveSection,
} from '../../services/aiResumeService';

const FILLER_WORDS = ['um', 'uh', 'like', 'you know', 'so', 'actually', 'basically', 'literally'];

export default function ResumeAnalyzer() {
  const [step, setStep] = useState('upload'); // upload | loading | results | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const handleFile = async (file) => {
    if (!file) return;
    setStep('loading');
    setErrorMsg('');

    try {
      const uploadData = await uploadResume(file);
      const resumeId = uploadData.resumeId;

      const [parsed, analysis] = await Promise.all([
        parseResume({ resumeId }),
        analyzeResume({ resumeId, parsedData: {} }),
      ]);

      setResult({ resumeId, fileName: uploadData.fileName, parsed, analysis });
      setActiveTab('overview');
      setStep('results');
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong analyzing the resume.');
      setStep('error');
    }
  };

  const reset = () => {
    setResult(null);
    setErrorMsg('');
    setStep('upload');
  };

  const pageStyle = {
    padding: '40px',
    background: 'linear-gradient(135deg, #0C1019 0%, #1B2231 100%)',
    minHeight: '100vh',
    color: '#fff',
  };

  return (
    <div style={pageStyle}>
      <h1 style={{ fontSize: '2.2em', marginBottom: '8px', color: '#00d4ff', textAlign: 'center' }}>
        📄 Resume Analyzer
      </h1>
      <p style={{ fontSize: '1em', marginBottom: '30px', color: '#b0b5ba', textAlign: 'center' }}>
        Ready to analyze your resume and unlock your potential!
      </p>

      {step === 'upload' && (
        <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
          <label
            style={{
              display: 'block',
              border: '2px dashed #2c3548',
              borderRadius: '12px',
              padding: '50px 20px',
              cursor: 'pointer',
              background: 'rgba(0,212,255,0.04)',
            }}
          >
            <div style={{ fontSize: '2em', marginBottom: '10px' }}>📤</div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Click to upload your resume</div>
            <div style={{ fontSize: '0.85em', color: '#8a90a0' }}>PDF, DOC, DOCX, or TXT — max 20MB</div>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              style={{ display: 'none' }}
              onChange={(e) => handleFile(e.target.files[0])}
            />
          </label>
        </div>
      )}

      {step === 'loading' && (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div
            style={{
              width: 48,
              height: 48,
              margin: '0 auto 20px',
              border: '4px solid #00d4ff',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#00d4ff', fontWeight: 'bold' }}>Analyzing your resume...</p>
        </div>
      )}

      {step === 'error' && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <p style={{ color: '#ff5c5c', marginBottom: '16px' }}>{errorMsg}</p>
          <button onClick={reset} style={btnStyle}>Try Again</button>
        </div>
      )}

      {step === 'results' && result && (
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{ textAlign: 'right', marginBottom: 16 }}>
            <button onClick={reset} style={btnStyle}>Analyze Another</button>
          </div>

          <TabBar active={activeTab} onChange={setActiveTab} />

          {activeTab === 'overview' && <OverviewTab result={result} />}
          {activeTab === 'chat' && <ChatTab resumeId={result.resumeId} />}
          {activeTab === 'questions' && <QuestionsTab resumeId={result.resumeId} />}
          {activeTab === 'jobmatch' && <JobMatchTab resumeId={result.resumeId} />}
          {activeTab === 'improve' && <ImproveTab resumeId={result.resumeId} />}
          {activeTab === 'speech' && <SpeechTab />}
        </div>
      )}
    </div>
  );
}

function TabBar({ active, onChange }) {
  const tabs = [
    { id: 'overview', label: '📊 Overview' },
    { id: 'chat', label: '💬 Chat' },
    { id: 'questions', label: '❓ Interview Qs' },
    { id: 'jobmatch', label: '🎯 Job Match' },
    { id: 'improve', label: '✍️ Improve Bullets' },
    { id: 'speech', label: '🎙 Speech Coach' },
  ];
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding: '8px 16px',
            borderRadius: '20px',
            border: '1px solid #2c3548',
            background: active === t.id ? 'linear-gradient(135deg, #00d4ff 0%, #0099ff 100%)' : 'transparent',
            color: active === t.id ? '#000' : '#b0b5ba',
            fontWeight: active === t.id ? 'bold' : 'normal',
            cursor: 'pointer',
            fontSize: '0.85em',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function OverviewTab({ result }) {
  return (
    <>
      <Card>
        <h2 style={{ color: '#00d4ff', marginBottom: 8 }}>
          {result.parsed.personalInfo.name || 'Candidate'}
        </h2>
        <p style={{ color: '#b0b5ba', fontSize: '0.9em' }}>
          {[result.parsed.personalInfo.email, result.parsed.personalInfo.phone]
            .filter(Boolean)
            .join(' · ') || 'No contact info detected'}
        </p>
      </Card>

      <Card>
        <h3 style={sectionTitle}>Scores</h3>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <ScoreBox label="ATS Score" value={result.analysis.atsScore} />
          <ScoreBox label="Overall Score" value={result.analysis.overallScore} />
          <ScoreBox label="Resume Health" value={result.analysis.resumeHealth} />
        </div>
      </Card>

      <Card>
        <h3 style={sectionTitle}>Experience</h3>
        <p>Years of experience: <strong>{result.analysis.yearsOfExperience}</strong></p>
        <p>Experience level: <strong>{result.analysis.experienceLevel}</strong></p>
        <p>Career level: <strong>{result.analysis.careerLevel}</strong></p>
      </Card>

      <Card>
        <h3 style={sectionTitle}>Skills</h3>
        <p style={{ color: '#8a90a0', fontSize: '0.85em', marginBottom: 6 }}>Technical</p>
        <ChipList items={result.parsed.skills.technical} empty="No technical skills detected" />
        <p style={{ color: '#8a90a0', fontSize: '0.85em', margin: '14px 0 6px' }}>Soft Skills</p>
        <ChipList items={result.parsed.skills.soft} empty="No soft skills detected" />
      </Card>

      {result.analysis.suggestions?.length > 0 && (
        <Card>
          <h3 style={sectionTitle}>Suggestions</h3>
          <ul style={{ paddingLeft: 20, color: '#d0d4db' }}>
            {result.analysis.suggestions.map((s, i) => (
              <li key={i} style={{ marginBottom: 6 }}>{s}</li>
            ))}
          </ul>
        </Card>
      )}

      {result.analysis.missingKeywords?.length > 0 && (
        <Card>
          <h3 style={sectionTitle}>Missing Keywords</h3>
          <ChipList items={result.analysis.missingKeywords} empty="None" tone="warn" />
        </Card>
      )}
    </>
  );
}

function ChatTab({ resumeId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: userMsg }]);
    setSending(true);
    try {
      const history = messages.map((m) => ({ role: m.role, content: m.text }));
      const res = await resumeChat({ resumeId, message: userMsg, conversationHistory: history });
      setMessages((prev) => [...prev, { role: 'assistant', text: res.message }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', text: `Error: ${err.message}` }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <h3 style={sectionTitle}>Chat with your Resume Coach</h3>
      <div style={{ maxHeight: 360, overflowY: 'auto', marginBottom: 12 }}>
        {messages.length === 0 && (
          <p style={{ color: '#6b7180', fontSize: '0.85em' }}>
            Ask anything — e.g. "How can I make my summary stronger?" or "What roles fit my background?"
          </p>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              marginBottom: 10,
              textAlign: m.role === 'user' ? 'right' : 'left',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                padding: '8px 14px',
                borderRadius: '14px',
                background: m.role === 'user' ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.06)',
                color: m.role === 'user' ? '#00d4ff' : '#d0d4db',
                maxWidth: '80%',
                fontSize: '0.9em',
              }}
            >
              {m.text}
            </span>
          </div>
        ))}
        {sending && <p style={{ color: '#6b7180', fontSize: '0.85em' }}>Thinking...</p>}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Type your question..."
          style={inputStyle}
        />
        <button onClick={send} disabled={sending} style={btnStyle}>Send</button>
      </div>
    </Card>
  );
}

function QuestionsTab({ resumeId }) {
  const [type, setType] = useState('technical');
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await generateInterviewQuestions({ resumeId, type });
      setQuestions(res.questions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <h3 style={sectionTitle}>Interview Questions Based on Your Resume</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['technical', 'behavioral', 'hr'].map((t) => (
          <button
            key={t}
            onClick={() => setType(t)}
            style={{
              ...btnStyle,
              background: type === t ? 'linear-gradient(135deg, #00d4ff 0%, #0099ff 100%)' : 'transparent',
              color: type === t ? '#000' : '#b0b5ba',
              border: '1px solid #2c3548',
            }}
          >
            {t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
        <button onClick={generate} disabled={loading} style={btnStyle}>
          {loading ? 'Generating...' : 'Generate'}
        </button>
      </div>
      {error && <p style={{ color: '#ff5c5c' }}>{error}</p>}
      {questions && questions.length > 0 && (
        <ol style={{ paddingLeft: 20, color: '#d0d4db' }}>
          {questions.map((q, i) => (
            <li key={i} style={{ marginBottom: 10 }}>
              {typeof q === 'string' ? q : q.text || JSON.stringify(q)}
            </li>
          ))}
        </ol>
      )}
      {questions && questions.length === 0 && (
        <p style={{ color: '#6b7180' }}>No questions generated — try again.</p>
      )}
    </Card>
  );
}

function JobMatchTab({ resumeId }) {
  const [jobDescription, setJobDescription] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const match = async () => {
    if (!jobDescription.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await matchJobDescription({ resumeId, jobDescription });
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <h3 style={sectionTitle}>Job Match Analysis</h3>
      <textarea
        value={jobDescription}
        onChange={(e) => setJobDescription(e.target.value)}
        placeholder="Paste a job description here..."
        rows={6}
        style={{ ...inputStyle, width: '100%', resize: 'vertical', marginBottom: 12 }}
      />
      <button onClick={match} disabled={loading} style={btnStyle}>
        {loading ? 'Analyzing...' : 'Check Match'}
      </button>
      {error && <p style={{ color: '#ff5c5c', marginTop: 12 }}>{error}</p>}
      {result && (
        <div style={{ marginTop: 16 }}>
          <pre style={{
            whiteSpace: 'pre-wrap',
            color: '#d0d4db',
            fontSize: '0.85em',
            background: 'rgba(255,255,255,0.03)',
            padding: 12,
            borderRadius: 8,
          }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </Card>
  );
}

function ImproveTab({ resumeId }) {
  const [section, setSection] = useState('summary');
  const [content, setContent] = useState('');
  const [improved, setImproved] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const improve = async () => {
    if (!content.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await improveSection({ resumeId, section, content });
      setImproved(res.improved || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <h3 style={sectionTitle}>Improve a Section</h3>
      <select
        value={section}
        onChange={(e) => setSection(e.target.value)}
        style={{ ...inputStyle, marginBottom: 12 }}
      >
        <option value="summary">Summary</option>
        <option value="experience">Experience Bullet</option>
        <option value="skills">Skills</option>
        <option value="project">Project Description</option>
      </select>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Paste the section text you want improved..."
        rows={5}
        style={{ ...inputStyle, width: '100%', resize: 'vertical', marginBottom: 12 }}
      />
      <button onClick={improve} disabled={loading} style={btnStyle}>
        {loading ? 'Improving...' : 'Improve with AI'}
      </button>
      {error && <p style={{ color: '#ff5c5c', marginTop: 12 }}>{error}</p>}
      {improved && (
        <div style={{ marginTop: 16, padding: 12, background: 'rgba(0,212,255,0.06)', borderRadius: 8 }}>
          <p style={{ color: '#00d4ff', fontSize: '0.8em', marginBottom: 6 }}>Improved version:</p>
          <p style={{ color: '#d0d4db', whiteSpace: 'pre-wrap' }}>{improved}</p>
        </div>
      )}
    </Card>
  );
}

function SpeechTab() {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState('');

  const recognitionRef = useRef(null);
  const startTimeRef = useRef(null);
  const lastWordTimeRef = useRef(null);
  const pauseCountRef = useRef(0);
  const longestPauseRef = useRef(0);

  const isSupported =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const startListening = () => {
    if (!isSupported) {
      setError("Speech recognition isn't supported in this browser. Try Chrome or Edge.");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    let fullTranscript = '';
    startTimeRef.current = Date.now();
    lastWordTimeRef.current = Date.now();
    pauseCountRef.current = 0;
    longestPauseRef.current = 0;

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          fullTranscript += text + ' ';
          const now = Date.now();
          const gap = (now - lastWordTimeRef.current) / 1000;
          if (gap > 1.5) {
            pauseCountRef.current += 1;
            if (gap > longestPauseRef.current) longestPauseRef.current = gap;
          }
          lastWordTimeRef.current = now;
        } else {
          interim += text;
        }
      }
      setTranscript(fullTranscript + interim);
    };

    recognition.onerror = (event) => {
      setError(`Recognition error: ${event.error}`);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      analyzeSpeech(fullTranscript);
    };

    recognition.start();
    recognitionRef.current = recognition;
    setIsListening(true);
    setFeedback(null);
    setError('');
    setTranscript('');
  };

  const stopListening = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
  };

  const analyzeSpeech = (text) => {
    const cleanText = text.trim();
    if (!cleanText) {
      setError('No speech detected. Try again and speak clearly.');
      return;
    }

    const words = cleanText.toLowerCase().split(/\s+/);
    const wordCount = words.length;
    const durationSeconds = (Date.now() - startTimeRef.current) / 1000;
    const wordsPerMinute = Math.round((wordCount / durationSeconds) * 60);

    const fillerCounts = {};
    let totalFillers = 0;
    FILLER_WORDS.forEach((filler) => {
      const regex = new RegExp(`\\b${filler.replace(' ', '\\s+')}\\b`, 'gi');
      const matches = cleanText.match(regex);
      if (matches) {
        fillerCounts[filler] = matches.length;
        totalFillers += matches.length;
      }
    });

    let paceTip = '';
    if (wordsPerMinute < 110) {
      paceTip = "You're speaking a bit slowly — try to add more energy and pace.";
    } else if (wordsPerMinute > 170) {
      paceTip = "You're speaking quite fast — slow down slightly so your points land clearly.";
    } else {
      paceTip = 'Good pace! This is a comfortable, confident speaking speed.';
    }

    let fillerTip = '';
    const fillerRate = totalFillers / (wordCount / 100);
    if (fillerRate > 5) {
      fillerTip = `You used filler words ${totalFillers} times. Try pausing silently instead of saying "um" or "like".`;
    } else if (totalFillers > 0) {
      fillerTip = `Just ${totalFillers} filler word${totalFillers > 1 ? 's' : ''} — pretty clean! Small room to tighten up further.`;
    } else {
      fillerTip = 'No filler words detected — very clean delivery!';
    }

    let pauseTip = '';
    if (longestPauseRef.current > 4) {
      pauseTip = `You had a pause of about ${Math.round(longestPauseRef.current)} seconds — try to keep pauses under 2-3 seconds.`;
    } else if (pauseCountRef.current > 0) {
      pauseTip = 'A few natural pauses — that\'s normal and can even sound thoughtful.';
    } else {
      pauseTip = 'Smooth, continuous delivery with no long pauses.';
    }

    let score = 100;
    score -= Math.min(totalFillers * 4, 30);
    if (wordsPerMinute < 100 || wordsPerMinute > 180) score -= 15;
    if (longestPauseRef.current > 4) score -= 15;
    score = Math.max(score, 30);

    setFeedback({ wordCount, durationSeconds: Math.round(durationSeconds), wordsPerMinute, totalFillers, fillerCounts, paceTip, fillerTip, pauseTip, score });
  };

  const scoreColor = feedback ? (feedback.score >= 80 ? '#3ddc84' : feedback.score >= 60 ? '#ffb020' : '#ff5c5c') : '#00d4ff';

  return (
    <Card>
      <h3 style={sectionTitle}>🎙 Speech Confidence Coach</h3>
      <p style={{ color: '#8a90a0', fontSize: '0.85em', marginBottom: 20 }}>
        Speak an interview answer out loud. Runs entirely in your browser — free, no limits, no AI quota used.
      </p>

      {!isSupported && (
        <p style={{ color: '#ffb020', fontSize: '0.85em', marginBottom: 16 }}>
          Speech recognition isn't supported in this browser. Please use Chrome or Edge.
        </p>
      )}
      {error && <p style={{ color: '#ff5c5c', fontSize: '0.85em', marginBottom: 16 }}>{error}</p>}

      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <button
          onClick={isListening ? stopListening : startListening}
          disabled={!isSupported}
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            border: 'none',
            fontSize: '1.6em',
            cursor: 'pointer',
            background: isListening ? '#ff5c5c' : 'linear-gradient(135deg, #00d4ff 0%, #0099ff 100%)',
            color: isListening ? '#fff' : '#000',
          }}
        >
          {isListening ? '⏹' : '🎤'}
        </button>
        <p style={{ color: '#8a90a0', fontSize: '0.85em', marginTop: 10 }}>
          {isListening ? 'Listening... click to stop' : 'Click the mic to start speaking'}
        </p>
      </div>

      {transcript && (
        <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 12, marginBottom: 16, color: '#d0d4db', fontSize: '0.9em', minHeight: 50 }}>
          {transcript}
        </div>
      )}

      {feedback && (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: '2.2em', fontWeight: 'bold', color: scoreColor }}>{feedback.score}</div>
            <div style={{ fontSize: '0.8em', color: '#8a90a0' }}>Confidence Score</div>
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <ScoreBox label="words/min" value={feedback.wordsPerMinute} />
            <ScoreBox label="filler words" value={feedback.totalFillers} />
            <ScoreBox label="duration (s)" value={feedback.durationSeconds} />
          </div>

          <div style={{ color: '#d0d4db', fontSize: '0.9em', lineHeight: 1.8 }}>
            <p>🏃 {feedback.paceTip}</p>
            <p>💬 {feedback.fillerTip}</p>
            <p>⏸ {feedback.pauseTip}</p>
          </div>

          {Object.keys(feedback.fillerCounts).length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p style={{ color: '#8a90a0', fontSize: '0.8em', marginBottom: 8 }}>Filler word breakdown</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(feedback.fillerCounts).map(([word, count]) => (
                  <span key={word} style={{ background: 'rgba(255,176,32,0.12)', color: '#ffb020', padding: '4px 12px', borderRadius: 20, fontSize: '0.8em' }}>
                    "{word}" × {count}
                  </span>
                ))}
              </div>
            </div>
          )}

          <button onClick={startListening} style={{ ...btnStyle, marginTop: 20, width: '100%' }}>
            Try Again
          </button>
        </>
      )}
    </Card>
  );
}

function Card({ children }) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid #232b3d',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
      }}
    >
      {children}
    </div>
  );
}

function ScoreBox({ label, value }) {
  const color = value >= 80 ? '#3ddc84' : value >= 60 ? '#ffb020' : '#ff5c5c';
  return (
    <div style={{ textAlign: 'center', flex: '1 1 100px' }}>
      <div style={{ fontSize: '1.8em', fontWeight: 'bold', color }}>{value}</div>
      <div style={{ fontSize: '0.8em', color: '#8a90a0' }}>{label}</div>
    </div>
  );
}

function ChipList({ items, empty, tone }) {
  if (!items || items.length === 0) {
    return <span style={{ color: '#6b7180', fontSize: '0.85em' }}>{empty}</span>;
  }
  const chipBg = tone === 'warn' ? 'rgba(255,92,92,0.12)' : 'rgba(0,212,255,0.1)';
  const chipColor = tone === 'warn' ? '#ff8080' : '#00d4ff';
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {items.map((item, i) => (
        <span
          key={i}
          style={{
            background: chipBg,
            color: chipColor,
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '0.85em',
          }}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

const sectionTitle = { color: '#fff', marginBottom: 12, fontSize: '1.1em' };
const btnStyle = {
  padding: '8px 18px',
  background: 'linear-gradient(135deg, #00d4ff 0%, #0099ff 100%)',
  color: '#000',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '0.85em',
};
const inputStyle = {
  flex: 1,
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1px solid #2c3548',
  background: 'rgba(255,255,255,0.03)',
  color: '#fff',
  fontSize: '0.9em',
};